import {
  Box,
  IconButton,
  Icons,
  iconSize,
  Menu,
  MenuItem,
  Stack,
  ToggleChip,
  Tooltip,
  palette,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SavedSearch, SmartListCount } from '@munifocus/contracts-gov';

/**
 * The dashboard's smart lists, each with how many files are in it right now:
 * the four every officer has ("My files", "My overdue", "Due this week",
 * "Awaiting service") and the searches they — or their team — saved.
 *
 * One chip is selected at a time; selecting the selected chip clears it. A
 * saved search the officer owns carries a menu to rename or delete it.
 */
export function SmartListStrip({
  lists,
  selected,
  onSelect,
  onRename,
  onDelete,
}: {
  lists: SmartListCount[];
  selected: string | null;
  onSelect: (key: string | null) => void;
  onRename: (search: SavedSearch) => void;
  onDelete: (search: SavedSearch) => void;
}) {
  const { t } = useTranslation();
  const [menu, setMenu] = useState<{ anchor: HTMLElement; search: SavedSearch } | null>(null);

  if (lists.length === 0) return null;

  const labelOf = (list: SmartListCount) =>
    list.kind === 'builtin' && list.builtin
      ? t(`gov.smartLists.builtin.${list.builtin}`)
      : (list.savedSearch?.name ?? '');

  return (
    <Box
      role="group"
      aria-label={t('gov.smartLists.label')}
      sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
    >
      {lists.map((list) => {
        const active = selected === list.key;
        const search = list.savedSearch;
        return (
          <Stack key={list.key} direction="row" sx={{ alignItems: 'center' }}>
            {search && (
              <Box
                component="span"
                title={
                  search.shared
                    ? t('gov.smartLists.sharedBy', { name: search.ownerName ?? '' })
                    : undefined
                }
                sx={{ display: 'inline-flex', mr: 0.5, color: palette.text.muted }}
                aria-hidden
              >
                {search.shared ? (
                  <Icons.Users size={iconSize.sm} />
                ) : (
                  <Icons.Bookmark size={iconSize.sm} />
                )}
              </Box>
            )}
            <ToggleChip
              selected={active}
              onToggle={() => onSelect(active ? null : list.key)}
              label={t('gov.smartLists.chipLabel', { name: labelOf(list), count: list.count })}
            />
            {search?.mine && (
              <Tooltip title={t('gov.smartLists.manage')}>
                <IconButton
                  size="small"
                  aria-label={t('gov.smartLists.manageNamed', { name: search.name })}
                  onClick={(e) => setMenu({ anchor: e.currentTarget, search })}
                >
                  <Icons.MoreVertical size={iconSize.sm} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      })}
      <Menu anchorEl={menu?.anchor ?? null} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            if (menu) onRename(menu.search);
            setMenu(null);
          }}
        >
          {t('gov.smartLists.rename')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) onDelete(menu.search);
            setMenu(null);
          }}
        >
          {t('common.actions.delete')}
        </MenuItem>
      </Menu>
    </Box>
  );
}
